import { useState } from "react";
import { toast } from "sonner";
import { siteUrl } from "./Seo";
import { API } from "../lib/api";

// Reusable, no-login badge embed block. Founders pick the badge style (we never
// swap their homepage UI behind their back) and get a live preview + copy-paste
// snippet with the chosen ?style= baked in. The Live Counter subtext is dynamic
// (milestone to the next 5-vote unlock). Shared by the tool detail callout and
// the public /badge hub.
const COUNTER_FLOOR = 5;

const STYLE_OPTIONS = [
  { value: "standard", label: "Featured On", desc: "Clean “featured on” badge — no vote count shown." },
  { value: "counter", label: "Live Counter", desc: "Live upvotes — shows “▲ VOTE” until 5 votes, then the number." },
  { value: "command", label: "Command-line", desc: "Launch command: >_ ⌘K + your tool name, on the TerminalValue terminal." },
];

export default function BadgeEmbed({ slug, upvotes = 0 }) {
  const [style, setStyle] = useState("standard");
  const active = STYLE_OPTIONS.find((o) => o.value === style) || STYLE_OPTIONS[0];

  const badgeUrl = `${siteUrl(`/api/badges/${slug}.svg`)}?style=${style}`;
  const linkUrl = siteUrl(`/tool/${slug}`);
  const previewSrc = `${API}/badges/${slug}.svg?style=${style}`;
  const snippet =
    `<a href="${linkUrl}" target="_blank" rel="noopener">\n` +
    `  <img src="${badgeUrl}" alt="Featured on TerminalValue.io" width="230" height="54">\n` +
    `</a>`;

  // Dynamic explanation for the Live Counter (gamified milestone).
  let descText = active.desc;
  if (active.value === "counter") {
    if (upvotes >= COUNTER_FLOOR) {
      descText = `Live upvote count — currently showing ▲ ${upvotes}.`;
    } else {
      const remaining = COUNTER_FLOOR - upvotes;
      descText = `Currently shows “▲ VOTE”. ${remaining} more upvote${remaining === 1 ? "" : "s"} (you're at ${upvotes}) and this badge automatically switches to your live count.`;
    }
  }

  return (
    <div data-testid="badge-embed">
      <div className="flex flex-wrap gap-2" data-testid="badge-style-toggle">
        {STYLE_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => setStyle(o.value)}
            className={`text-[12px] px-2 py-1 border transition-colors ${
              style === o.value
                ? "bg-black text-white border-black"
                : "border-[#ccc] text-[#444] hover:border-black"
            }`}
            data-testid={`badge-style-${o.value}`}
            aria-pressed={style === o.value}
          >
            [{o.label}]
          </button>
        ))}
      </div>

      <p className="text-[11px] text-[#888] mt-1 m-0" data-testid="badge-style-desc">
        {descText}
      </p>

      <div className="mt-3" data-testid="badge-embed-preview">
        <img src={previewSrc} alt="Featured on TerminalValue.io" width="230" height="54" />
      </div>
      <pre
        className="mt-3 p-2 border border-black bg-[#FBF8EE] text-[11px] overflow-x-auto whitespace-pre"
        data-testid="badge-embed-code"
      >
        {snippet}
      </pre>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(snippet);
            toast.success("badge embed code copied");
          } catch {
            toast.error("could not copy");
          }
          // Log install intent (best-effort, non-blocking).
          fetch(`${API}/badges/${slug}/copied`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ style }),
          }).catch(() => {});
        }}
        className="tv-btn mt-2"
        data-testid="badge-embed-copy"
      >
        [copy embed code]
      </button>
    </div>
  );
}
